import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCarpetFloorCovering,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasConcreteFloorCovering,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHardwoodFloorCovering,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLaminateFloorCovering,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLinoleumFloorCovering,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSlateFloorCovering,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSoftwoodFloorCovering,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTileFloorCovering,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasVinylFloorCovering,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { DEFAULT_ERROR_MESSAGE } from '@zg-rentals/zillow-rental-manager/constants';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';

import {
  FLOOR_COVERING_KEYS,
  FLOOR_COVERING_METADATA,
  type FloorCoveringKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

import { type AmenityFlagSave } from '../../utils';

type LocalState = Record<FloorCoveringKey, boolean>;

const FloorsAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const floorCoveringTypes = listingDetails?.buildingAttributes?.floorCoveringTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasHardwood: floorCoveringTypes?.hasHardwood ?? false,
    hasTile: floorCoveringTypes?.hasTile ?? false,
    hasCarpet: floorCoveringTypes?.hasCarpet ?? false,
    hasLaminate: floorCoveringTypes?.hasLaminate ?? false,
    hasLinoleum: floorCoveringTypes?.hasLinoleum ?? false,
    hasVinyl: floorCoveringTypes?.hasVinyl ?? false,
    hasSlate: floorCoveringTypes?.hasSlate ?? false,
    hasSoftwood: floorCoveringTypes?.hasSoftwood ?? false,
    hasConcrete: floorCoveringTypes?.hasConcrete ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Floor covering updated',
  });

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.AMENITIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const { mutateAsync: updateHardwood } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHardwoodFloorCovering,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHardwoodFloorCovering',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateTile } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTileFloorCovering,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTileFloorCovering',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateCarpet } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCarpetFloorCovering,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCarpetFloorCovering',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateLaminate } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLaminateFloorCovering,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLaminateFloorCovering',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateLinoleum } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLinoleumFloorCovering,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLinoleumFloorCovering',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateVinyl } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasVinylFloorCovering,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasVinylFloorCovering',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateSlate } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSlateFloorCovering,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSlateFloorCovering',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateSoftwood } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSoftwoodFloorCovering,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSoftwoodFloorCovering',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateConcrete } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasConcreteFloorCovering,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasConcreteFloorCovering',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasHardwood: updateHardwood,
    hasTile: updateTile,
    hasCarpet: updateCarpet,
    hasLaminate: updateLaminate,
    hasLinoleum: updateLinoleum,
    hasVinyl: updateVinyl,
    hasSlate: updateSlate,
    hasSoftwood: updateSoftwood,
    hasConcrete: updateConcrete,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as FloorCoveringKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = FLOOR_COVERING_METADATA[fieldName];
      if (isFeedListing) {
        await setFeedAmenity(metadata.apiKey, checked);
      } else {
        await mutationMap[fieldName]({
          postData: {
            [metadata.apiKey]: checked,
            listingAlias,
          },
        });
      }
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(err, DEFAULT_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Floors</Legend>} disabled={isInputFormDisabled}>
      {FLOOR_COVERING_KEYS.map((fieldName) => {
        const metadata = FLOOR_COVERING_METADATA[fieldName];
        return (
          <LabeledControl
            key={`floorCoveringTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(FloorsAmenities);
